import { useRouter } from 'next/router'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationCreateBookingDocument,
  useMutationCreateCloudflareDocument,
  useMutationDeleteBookingDocument,
  useMutationUpdateBookingDocument,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreBookingDocument,
  type CoreBookingDocumentCategory,
  type CoreUserDocument,
  type UNSAFE_any,
} from '@flecto/types'

import { useBookingStore } from '../../booking-details/hooks/useBookingStore'

export type UploadDocumentStatus = {
  id: string
  status: string
  error: {
    id: string
    message: string
  }
  document: CoreBookingDocument
  cloudflareDocument?: CoreUserDocument
  fileDetails: UNSAFE_any
}

type ErrorKey = 'file-invalid-type' | 'file-too-large'

export const useBookingDocumentActions = () => {
  const track = useAnalytics('segment')
  const { t } = useTranslation('saas', { keyPrefix: 'booking' })
  const { mutateAsync: createBooking } = useMutationCreateBookingDocument()
  const { mutateAsync: createCloudflareDocument } =
    useMutationCreateCloudflareDocument()
  const { mutateAsync: deleteBookingDocument } =
    useMutationDeleteBookingDocument()
  const { mutateAsync: updateBookingDocument } =
    useMutationUpdateBookingDocument()

  const router = useRouter()
  const id = String(router.query?.id)

  const { documents: data, setDocuments } = useBookingStore()

  const [files, setFiles] = React.useState<CoreBookingDocument[] | null>(data)
  const [uploadedFiles, setUploadedFiles] = React.useState<
    UploadDocumentStatus[]
  >([])
  const [abortControllers, setAbortControllers] = React.useState<
    Record<string, AbortController>
  >({})

  React.useEffect(() => {
    if (data?.length) {
      setFiles(data)
    }
  }, [data])

  const updateUploadedFile = (
    id: string,
    updates: Partial<UploadDocumentStatus>
  ) => {
    setUploadedFiles((prev) =>
      prev.map((uploadedFile) =>
        uploadedFile.id === id ? { ...uploadedFile, ...updates } : uploadedFile
      )
    )
  }

  const handleUpload = (filesToUpload: UNSAFE_any[] = []) => {
    const mappedFiles = filesToUpload?.map((file) => {
      const uniqueId = uuidv4()
      const newUploadStatus: UploadDocumentStatus = {
        id: uniqueId,
        status: 'loading',
        error: { id: '', message: '' },
        document: {
          filename_base: file.name,
          visible_to_renter: false,
          category: 'others',
        } as CoreBookingDocument,
        fileDetails: file,
      }
      setUploadedFiles((prev) => [...prev, newUploadStatus])
      return { file, id: uniqueId }
    })

    mappedFiles.forEach(async (file) => {
      const startTime = Date.now()
      const controller = new AbortController()
      setAbortControllers((prev) => ({ ...prev, [file.id]: controller }))

      try {
        const response = await createCloudflareDocument({
          file: file.file,
          signal: controller.signal,
        })
        const bookingResponse = await createBooking({
          bookingId: id,
          document: {
            file_id: response.blob_signed_id,
            visible_to_renter: false,
            category: 'others' as CoreBookingDocumentCategory,
          },
          signal: controller.signal,
        })
        updateUploadedFile(file.id, {
          status: 'success',
          document: bookingResponse,
          cloudflareDocument: response,
        })
      } catch (error: UNSAFE_any) {
        if (error.name === 'CanceledError') {
          setUploadedFiles((prev) =>
            prev.filter((uploadedFile) => uploadedFile.id !== file.id)
          )
        } else {
          updateUploadedFile(file.id, {
            status: 'error',
            error: {
              id: 'upload_failed',
              message: error.message,
            },
          })
        }
        const endTimeFailed = Date.now()
        track('BOOKING_DOCUMENT__ADDED_ATTEMPT', {
          filename: file.file.name,
          type: file.file.type,
          category: 'others',
          size: file.file.size,
          uploadTime: endTimeFailed - startTime,
          status: 'error',
        })
      } finally {
        setAbortControllers((prev) => {
          const { [file.id]: _, ...rest } = prev
          return rest
        })
      }
    })
  }

  const handleView = (link: string) => {
    window.open(link, '_blank')
  }

  const handleRejection = (fileRejections: UNSAFE_any[]) => {
    const errorMap: Record<ErrorKey, string> = {
      'file-invalid-type': t('booking_documents.file_invalid_type'),
      'file-too-large': t('booking_documents.file_invalid_size'),
    }
    const rejectedFiles = fileRejections?.map((file) => {
      track('BOOKING_DOCUMENT__ADDED_ATTEMPT', {
        filename: file.file.name,
        type: file.file.type,
        category: 'others',
        size: file.file.size,
        uploadTime: 0,
        status: 'error',
      })
      return {
        id: uuidv4(),
        status: 'error',
        error: {
          id: file.errors[0]?.code,
          message: errorMap[file.errors[0]?.code as ErrorKey],
        },
        document: {
          filename_base: file.file.name,
          visible_to_renter: false,
          category: 'others' as CoreBookingDocumentCategory,
        } as CoreBookingDocument,
        fileDetails: file.file,
      }
    })
    setUploadedFiles((prev) => [...prev, ...rejectedFiles])
  }

  const handleDelete = async (documentId: string) => {
    await deleteBookingDocument(
      { bookingId: id, documentId },
      {
        onSuccess: () => {
          setFiles((prev) =>
            prev ? prev.filter((file) => file.id !== documentId) : []
          )
          setUploadedFiles((prev) =>
            prev.filter((file) => file.document.id !== documentId)
          )
        },
      }
    )
  }

  const deleteUploaded = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId))
  }

  const handleChange = async (
    documentId: string,
    field: string,
    value: string | boolean
  ) => {
    const documentToEdit = files?.find((file) => file.id === documentId)

    const updatedDocument = { ...documentToEdit, [field]: value }
    const updatedFile = await updateBookingDocument({
      documentId,
      bookingId: id,
      document: updatedDocument,
    })

    const updatedDocuments = files
      ? files?.map((doc) => (doc.id === documentId ? updatedFile : doc))
      : [updatedFile]

    setDocuments(updatedDocuments)
    setFiles(updatedDocuments)

    setUploadedFiles((prev) =>
      prev.map((uploadedFile) =>
        uploadedFile.document.id === documentId
          ? { ...uploadedFile, document: updatedFile }
          : uploadedFile
      )
    )
  }

  const handleRetry = async (fileId: string) => {
    const documentToRetry = uploadedFiles.find((file) => file.id === fileId)
    if (documentToRetry) {
      setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId))
      await handleUpload([documentToRetry.fileDetails])
    }
  }

  const handleCancel = (fileId: string) => {
    const controller = abortControllers[fileId]
    if (controller) {
      controller.abort()
    }
  }

  return {
    uploadedFiles,
    view: handleView,
    deleteDocument: handleDelete,
    change: handleChange,
    files,
    handleUpload,
    handleRejection,
    deleteUploaded,
    handleRetry,
    handleCancel,
  }
}
